import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import { FileUp, Loader2 } from "lucide-react";
import {
  CertificationButton,
  CertificationInputField,
  CertificationModal,
  CertificationTextareaField,
} from "../../ui";
import type {
  ManualSistemaIntegrado,
  ManualSistemaIntegradoUploadPayload,
} from "../types/manual-sistema-integrado";

type Props = {
  open: boolean;
  manual: ManualSistemaIntegrado | null;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: ManualSistemaIntegradoUploadPayload) => void;
};

function today(): string {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);

  return localDate.toISOString().slice(0, 10);
}

export function ManualSistemaIntegradoUploadModal({
  open,
  manual,
  submitting,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [documento, setDocumento] = useState<File | null>(null);
  const [edicion, setEdicion] = useState("");
  const [fechaEdicion, setFechaEdicion] = useState(today());
  const [modificaciones, setModificaciones] = useState("");

  useEffect(() => {
    if (!open) return;
    setDocumento(null);
    setEdicion(manual?.edicion ?? "");
    setFechaEdicion(today());
    setModificaciones("");
  }, [manual, open]);

  const valid =
    documento !== null &&
    documento.name.toLowerCase().endsWith(".docx") &&
    edicion.trim() !== "" &&
    fechaEdicion !== "" &&
    modificaciones.trim() !== "";

  return (
    <CertificationModal
      open={open}
      title={manual ? "Sustituir el manual" : "Cargar el manual"}
      eyebrow="Documento compartido"
      description={
        manual
          ? "El nuevo archivo DOCX sustituirá el manual vigente cuando la carga finalice correctamente."
          : "Carga el archivo DOCX inicial del Manual del Sistema Integrado."
      }
      size="wide"
      onClose={onClose}
      onSubmit={() => {
        if (!valid || documento === null) return;
        onSubmit({
          documento,
          edicion: edicion.trim(),
          fecha_edicion: fechaEdicion,
          modificaciones_realizadas: modificaciones.trim(),
        });
      }}
      submitting={submitting}
      error={error}
      closeOnBackdrop={false}
      footer={
        <>
          <CertificationButton variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </CertificationButton>
          <CertificationButton
            type="submit"
            variant="primary"
            icon={submitting ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />}
            disabled={!valid || submitting}
          >
            {manual ? "Sustituir manual" : "Cargar manual"}
          </CertificationButton>
        </>
      }
    >
      <div className="manual-si-shared-form-grid">
        <CertificationInputField
          label="Archivo Word"
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          disabled={submitting}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setDocumento(event.target.files?.[0] ?? null)}
          hint="Solo DOCX. Tamaño máximo: 50 MB."
          fullWidth
          wrapperClassName="manual-si-upload-file-field"
        />
        <CertificationInputField
          label="Edición"
          type="text"
          value={edicion}
          maxLength={50}
          disabled={submitting}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setEdicion(event.target.value)}
          placeholder="Ej. 06/10/2025"
        />
        <CertificationInputField
          label="Fecha de edición"
          type="date"
          value={fechaEdicion}
          disabled={submitting}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setFechaEdicion(event.target.value)}
        />
        <CertificationTextareaField
          label="Modificaciones realizadas"
          value={modificaciones}
          rows={5}
          disabled={submitting}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setModificaciones(event.target.value)}
          placeholder="Resume los cambios incluidos en esta edición."
          fullWidth
        />
      </div>
    </CertificationModal>
  );
}
